import React from 'react';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {sweetAPI} from 'app/store/sweet-api';
import {anyRelatedMatch, runWhenAll} from 'app/subscriptions';
import {
  POLICIES_NEW_POLICY,
  PROCEDURES_ADD_NEW,
  PROCESS_CREATE_PAGE_URL,
} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {getPolicyName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {
  POLICY_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
  PROCESS_RTK_TAGS,
} from 'app/utils/rtk-tags';

import {RTKGenericAttachedItemsSidebar} from './GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';

import type {GenericActionableType} from './GenericAttachedItemsSidebar/GenericAttachedItemsSidebarItem';
import type {RelatedPolicyType} from 'app/policies/types';
import type {UseListQueryType} from 'app/store/sweet-api/api-query';
import type {ModelChangeMessageType} from 'app/subscriptions';
import type {ProcedureSerializedType, ProcessSerializedType} from 'sweet-types';

type Props<
  ItemType extends GenericActionableType,
  FetchParams extends {page: string},
  UseQueryHookType extends UseListQueryType<FetchParams, ItemType>
> = {
  attachedItems: Array<
    RelatedPolicyType | ProcedureSerializedType | ProcessSerializedType
  >;
  closeUrl: string;
  detailPageObjectId: string | number;
  canEdit: boolean;
  sidebarContentType: 'procedure' | 'proceduregroup' | 'policy';
  detailPageContentType: 'procedure' | 'proceduregroup' | 'policy';
  showFilterField: boolean;
  useDataHook: UseQueryHookType;
  paramsConverter: (query: Record<string, unknown>) => FetchParams;
  onRemove: (item: ItemType) => Promise<unknown>;
  onAttach: (item: ItemType) => Promise<unknown>;
  legacyRefetchData?: () => Promise<unknown>;
};

const CREATE_LINK = {
  policy: POLICIES_NEW_POLICY,
  procedure: PROCEDURES_ADD_NEW,
  proceduregroup: PROCESS_CREATE_PAGE_URL,
};

const INVALIDATE_TAG = {
  policy: POLICY_RTK_TAGS.TAGS,
  procedure: PROCEDURE_RTK_TAGS.TAGS,
  proceduregroup: PROCESS_RTK_TAGS.TAGS,
};

export const DetailPageGenericPoliciesSidebar = <
  ItemType extends GenericActionableType,
  FetchParams extends {page: string},
  UseQueryHookType extends UseListQueryType<FetchParams, ItemType>
>({
  attachedItems,
  detailPageObjectId,
  detailPageContentType,
  canEdit,
  showFilterField,
  sidebarContentType,
  closeUrl,
  useDataHook,
  paramsConverter,
  onAttach,
  onRemove,
  legacyRefetchData,
}: Props<ItemType, FetchParams, UseQueryHookType>) => {
  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);

  const ctSidebar = getContentType(sidebarContentType);
  const ctDetailPage = getContentType(detailPageContentType);
  return (
    <RTKGenericAttachedItemsSidebar
      closeUrl={closeUrl}
      useDataHook={useDataHook}
      paramsConverter={paramsConverter}
      attachedItems={attachedItems}
      title={ctSidebar.pluralName}
      itemNameProcessor={getPolicyName}
      handleModelChange={(message: ModelChangeMessageType) => {
        runWhenAll(
          message,
          [
            anyRelatedMatch({
              content_type: detailPageContentType,
              id:
                typeof detailPageObjectId === 'string'
                  ? parseInt(detailPageObjectId)
                  : detailPageObjectId,
            }),
          ],
          () => {
            if (legacyRefetchData != null) {
              legacyRefetchData();
            }
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: INVALIDATE_TAG[sidebarContentType], id: 'list'},
            ]);
          }
        );
      }}
      onRemove={onRemove}
      itemComponentProps={(policy, attached) => {
        return {
          disableAndShowTooltipText: !canEdit
            ? gt(
                'You have to be a %s manager to add or remove %s',
                ctDetailPage.name,
                ctSidebar.pluralName
              )
            : undefined,
          actionTooltip: attached
            ? gt('Remove this %s from %s', ctSidebar.name, ctDetailPage.name)
            : gt('Add this %s to %s', ctSidebar.name, ctDetailPage.name),
        };
      }}
      showFilterField={showFilterField}
      searchPlaceholder={gt('Filter %s...', ctSidebar.pluralName)}
      onAttach={onAttach}
      canCreate={isTeamMember}
      createLabel={gt('Create %s', ctSidebar.name)}
      createURL={CREATE_LINK[sidebarContentType]}
      emptyResultsComponents={
        <NoResultsMessage
          onSidebar={true}
          icon="fa-umbrella"
          head={gt('No %s', ctSidebar.pluralName)}
        >
          {showFilterField
            ? gt(
                'You can attach and detach %s from this %s or create a new policy' +
                  ' using the button below',
                ctSidebar.pluralName,
                ctDetailPage.name
              )
            : null}
        </NoResultsMessage>
      }
    />
  );
};
